import { h, Component } from 'preact';
import { Text } from 'preact-i18n';
import { createGame, draw, move, undo, movableCards, SUITS, isRed } from '../../lib/arcade/solitaireEngine';
import { formatTime } from '../../lib/arcade/formatTime';
import ArcadeLeaderboard from './leaderboard';
import './solitaire.css';

const SYMBOLS = { spades: '♠', hearts: '♥', clubs: '♣', diamonds: '♦' };
const rankLabel = rank => ({ 1: 'A', 11: 'J', 12: 'Q', 13: 'K' }[rank] || String(rank));

function CardFace({ card }) {
    return <span class='sol-card-face' aria-hidden='true'>
        <span class='sol-corner'>{rankLabel(card.rank)}{SYMBOLS[card.suit]}</span>
        <span class='sol-suit'>{SYMBOLS[card.suit]}</span>
        <span class='sol-corner sol-bottom'>{rankLabel(card.rank)}{SYMBOLS[card.suit]}</span>
    </span>;
}

/** Kabal belongs to the arcade. A run token keeps late score responses out of a new deal. */
class Solitaire extends Component {
    constructor(props) {
        super(props);
        this.game = createGame();
        this.state = {
            revision: 0, selected: null, elapsedMs: 0, paused: false, invalid: false,
            submitting: false, saveFailed: false, loadFailed: false, result: null,
            leaderboard: { data: [], me: null },
        };
        this.run = 0;
        this.scoreRequest = 0;
        this.accrued = 0;
        this.startedAt = null;
        this.timer = null;
        this.paused = false;
        this.left = false;
        this.unmounted = false;
        this.submitted = false;
        this.dragSource = null;
    }

    componentDidMount() {
        window.addEventListener('blur', this.pause);
        window.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.loadScores();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopClock();
        window.removeEventListener('blur', this.pause);
        window.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    current = run => run === this.run && !this.left && !this.unmounted;

    elapsed = () => this.accrued + (this.startedAt === null ? 0 : Math.max(0, performance.now() - this.startedAt));

    startClock = () => {
        if (this.startedAt !== null) return;
        this.startedAt = performance.now();
        this.timer = setInterval(() => this.setState({ elapsedMs: this.elapsed() }), 200);
    };

    stopClock = () => {
        this.accrued = this.elapsed();
        this.startedAt = null;
        clearInterval(this.timer);
        this.timer = null;
    };

    loadScores = async () => {
        const run = this.run;
        const request = ++this.scoreRequest;
        try {
            const response = await this.props.stores.arcadeScoreStore?.loadLeaderboard(this.game.game);
            if (!this.current(run) || request !== this.scoreRequest) return;
            this.setState({
                loadFailed: response?.status !== 200,
                leaderboard: response?.status === 200 ? response : { data: [], me: null },
            });
        } catch (error) {
            if (this.current(run) && request === this.scoreRequest) this.setState({ loadFailed: true });
        }
    };

    refresh = (extra = {}) => this.setState(previous => ({ revision: previous.revision + 1, ...extra }));

    reset = (drawCount = this.game.drawCount) => {
        this.stopClock();
        this.run += 1;
        this.game = createGame({ drawCount });
        this.accrued = 0;
        this.paused = false;
        this.submitted = false;
        this.dragSource = null;
        this.refresh({
            selected: null, elapsedMs: 0, paused: false, invalid: false,
            submitting: false, saveFailed: false, loadFailed: false, result: null,
            leaderboard: { data: [], me: null },
        });
        this.loadScores();
    };

    interactive = () => !this.paused && !this.left && !this.unmounted && this.game.status !== 'won';

    apply = (next) => {
        if (!this.interactive() || next === this.game) return false;
        this.startClock();
        this.game = next;
        this.refresh({ selected: null, invalid: false });
        if (next.status === 'won') {
            this.stopClock();
            this.submitWin();
        }
        return true;
    };

    drawCards = () => this.apply(draw(this.game));

    undoMove = () => this.apply(undo(this.game));

    selectCard = (source) => {
        if (!this.interactive()) return;
        const selected = this.state.selected;
        if (selected && selected.pile === source.pile && selected.index === source.index
            && selected.cardIndex === source.cardIndex) {
            this.setState({ selected: null, invalid: false });
            return;
        }
        if (selected && this.apply(move(this.game, selected, source))) return;
        if (movableCards(this.game, source).length) {
            this.startClock();
            this.setState({ selected: source, invalid: false });
        } else if (selected) this.setState({ invalid: true });
    };

    selectPile = (destination) => {
        if (!this.interactive() || !this.state.selected) return;
        if (!this.apply(move(this.game, this.state.selected, destination))) this.setState({ invalid: true });
    };

    beginDrag = (event, source) => {
        if (!this.interactive() || !movableCards(this.game, source).length) {
            event.preventDefault();
            return;
        }
        this.startClock();
        this.dragSource = source;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', 'solitaire-card');
        this.setState({ selected: source, invalid: false });
    };

    dragOver = (event) => {
        if (!this.dragSource || !this.interactive()) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    drop = (event, destination) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.dragSource && this.interactive()
            && !this.apply(move(this.game, this.dragSource, destination))) this.setState({ invalid: true });
        this.dragSource = null;
    };

    endDrag = () => { this.dragSource = null; };

    submitWin = async () => {
        if (this.game.status !== 'won' || this.submitted) return;
        this.submitted = true;
        const run = this.run;
        const durationMs = Math.max(1, Math.round(this.accrued));
        this.accrued = durationMs;
        this.setState({ elapsedMs: durationMs, submitting: true });
        try {
            const response = await this.props.stores.arcadeScoreStore?.submitScore({
                game: this.game.game, score: durationMs, durationMs, wave: 1, won: true,
            });
            if (!this.current(run)) return;
            const saved = response?.status === 200 || response?.status === 201;
            this.setState({ submitting: false, saveFailed: !saved, result: saved ? response.data : null });
            if (saved) await this.loadScores();
        } catch (error) {
            if (this.current(run)) this.setState({ submitting: false, saveFailed: true });
        }
    };

    pause = () => {
        if (!this.interactive() || this.startedAt === null) return;
        this.stopClock();
        this.paused = true;
        this.dragSource = null;
        this.setState({ paused: true, selected: null, elapsedMs: this.accrued });
    };

    resume = () => {
        if (!this.paused || this.left) return;
        this.paused = false;
        this.startClock();
        this.setState({ paused: false });
    };

    onVisibility = () => { if (document.hidden) this.pause(); };

    onKeyDown = (event) => {
        if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.code === 'KeyP') {
            event.preventDefault();
            if (this.paused) this.resume();
            else this.pause();
        } else if (event.code === 'KeyU') {
            event.preventDefault();
            this.undoMove();
        } else if (event.code === 'Escape') this.setState({ selected: null, invalid: false });
    };

    leave = (callback) => {
        this.left = true;
        this.dragSource = null;
        this.stopClock();
        callback?.();
    };

    back = () => this.leave(this.props.onBack);

    close = () => this.leave(() => this.props.stores.appState.toggleDrawer(false, this.props.drawerLevel || 1));

    renderCard(card, source, style = {}) {
        const selected = this.state.selected;
        const highlighted = selected && selected.pile === source.pile && selected.index === source.index
            && source.cardIndex >= selected.cardIndex;
        const playable = this.interactive() && movableCards(this.game, source).length > 0;
        return <button key={card.id} type='button'
            class={`sol-card ${card.faceUp ? (isRed(card) ? 'sol-red' : 'sol-black') : 'sol-back'}
                ${highlighted ? 'sol-selected' : ''}`}
            style={style} data-card={`${source.pile}-${source.index || 0}-${source.cardIndex}`}
            disabled={!card.faceUp || (source.pile === 'waste' && !playable)}
            aria-pressed={!!highlighted} draggable={playable}
            onClick={(event) => { event.stopPropagation(); this.selectCard(source); }}
            onDragStart={event => this.beginDrag(event, source)} onDragEnd={this.endDrag}>
            {card.faceUp ? <CardFace card={card} /> : <span aria-hidden='true' class='sol-back-pattern' />}
            <span class='visually-hidden'>
                {card.faceUp ? <span>{rankLabel(card.rank)} <Text id={`arcade.sol${card.suit}`}>
                    {card.suit}
                </Text></span> : <Text id='arcade.solFaceDown'>Face-down card</Text>}
            </span>
        </button>;
    }

    renderBoard() {
        const { stock, waste, tableau, foundations, drawCount, status } = this.game;
        return <div class='sol-board' data-board>
            <div class='sol-top'>
                <button type='button' data-stock
                    class={`sol-card sol-stock ${stock.length > 0 ? 'sol-back' : 'sol-slot'}`}
                    disabled={status === 'won' || (stock.length === 0 && waste.length === 0)} onClick={this.drawCards}>
                    {stock.length > 0 ? <span class='sol-back-pattern' aria-hidden='true' />
                        : <span class='sol-recycle' aria-hidden='true'>↻</span>}
                    <span class='visually-hidden'>
                        {stock.length > 0 ? <Text id='arcade.solDraw'>Draw cards</Text>
                            : <Text id='arcade.solRecycle'>Recycle the waste pile</Text>}
                    </span>
                    {stock.length > 0 && <span class='sol-stock-count' aria-hidden='true'>{stock.length}</span>}
                </button>
                <div class='sol-waste'>
                    <div class='sol-waste-inner'>
                        <div class='sol-slot sol-waste-slot' aria-hidden='true' />
                        {waste.slice(-drawCount).map((card, i, cards) => this.renderCard(card, {
                            pile: 'waste', index: 0, cardIndex: waste.length - cards.length + i,
                        }, { left: `${i * 24}%` }))}
                    </div>
                </div>
                {foundations.map((pile, index) => <div key={SUITS[index]} class='sol-foundation'
                    data-foundation={index} onDragOver={this.dragOver}
                    onDrop={event => this.drop(event, { pile: 'foundations', index })}>
                    {pile.length > 0 ? this.renderCard(pile[pile.length - 1], {
                        pile: 'foundations', index, cardIndex: pile.length - 1,
                    }) : <button type='button' class='sol-card sol-slot'
                        onClick={() => this.selectPile({ pile: 'foundations', index })}>
                        <span class='sol-slot-suit' aria-hidden='true'>{SYMBOLS[SUITS[index]]}</span>
                        <span class='visually-hidden'><Text id='arcade.solFoundation'>Foundation</Text>{' '}
                            <Text id={`arcade.sol${SUITS[index]}`}>{SUITS[index]}</Text>
                        </span>
                    </button>}
                </div>)}
            </div>
            <div class='sol-tableau'>
                {tableau.map((pile, index) => {
                    const hidden = pile.filter(card => !card.faceUp).length;
                    const topOffset = cardIndex => `calc(${Math.min(cardIndex, hidden)} * var(--sol-hidden-step)
                        + ${Math.max(0, cardIndex - hidden)} * var(--sol-step))`;
                    return <div key={index} class='sol-column' data-column={index}
                        style={{ paddingBottom: topOffset(Math.max(0, pile.length - 1)) }}
                        onDragOver={this.dragOver} onDrop={event => this.drop(event, { pile: 'tableau', index })}>
                        {pile.length === 0 && <button type='button' class='sol-card sol-slot'
                            onClick={() => this.selectPile({ pile: 'tableau', index })}>
                            <span aria-hidden='true' class='sol-slot-suit'>K</span>
                            <span class='visually-hidden'><Text id='arcade.solEmptyColumn'>Empty column: king</Text>
                                {' '}{index + 1}</span>
                        </button>}
                        {pile.map((card, cardIndex) => this.renderCard(card, {
                            pile: 'tableau', index, cardIndex,
                        }, { top: topOffset(cardIndex) }))}
                    </div>;
                })}
            </div>
        </div>;
    }

    render() {
        const { paused, elapsedMs, selected, invalid, submitting, saveFailed, loadFailed, result } = this.state;
        const { drawCount, moves, history, status } = this.game;
        const won = status === 'won';
        return <div class='solitaire-game' data-bs-theme='dark'>
            <div class='sol-window'>
                <header class='sol-header'>
                    <span aria-hidden='true'>♠ ♥</span>
                    <h2><Text id='arcade.solitaire'>Solitaire</Text></h2>
                    <span class='sol-subtitle'>KLONDIKE</span>
                </header>
                <div class='sol-toolbar'>
                    <div class='sol-actions'>
                        <button type='button' class='sol-control' data-reset onClick={() => this.reset()}>
                            <Text id='arcade.solNewGame'>New game</Text>
                        </button>
                        <button type='button' class='sol-control' data-undo
                            disabled={paused || won || history.length === 0}
                            onClick={this.undoMove}><Text id='arcade.solUndo'>Undo</Text></button>
                        <button type='button' class='sol-control' data-pause
                            disabled={won || (!paused && this.startedAt === null)}
                            onClick={paused ? this.resume : this.pause}>
                            {paused ? <Text id='arcade.resume'>Resume</Text> : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                    </div>
                    <div class='sol-actions'>
                        {[1, 3].map(count => <button key={count} type='button'
                            class={`sol-control ${drawCount === count ? 'sol-mode-active' : ''}`}
                            data-draw-count={count} aria-pressed={drawCount === count}
                            onClick={() => { if (drawCount !== count) this.reset(count); }}>
                            {count === 1 ? <Text id='arcade.solDrawOne'>Draw one</Text>
                                : <Text id='arcade.solDrawThree'>Draw three</Text>}
                        </button>)}
                    </div>
                </div>
                <div class='sol-hud'>
                    <span><Text id='arcade.solMoves'>Moves</Text>: <strong data-moves>{moves}</strong></span>
                    <span><Text id='arcade.solTime'>Time</Text>: <strong data-timer>
                        {formatTime(elapsedMs).split('.')[0]}</strong></span>
                </div>
                <div class='sol-table'>
                    {paused ? <div class='sol-paused'>
                        <Text id='arcade.paused'>Paused — press P or Resume to continue</Text>
                        <button type='button' class='sol-control' onClick={this.resume}>
                            <Text id='arcade.resume'>Resume</Text>
                        </button>
                    </div> : this.renderBoard()}
                    {won && <div class='sol-win' role='status'>
                        <div class='sol-celebration' aria-hidden='true'>
                            {Array.from({ length: 12 }, (_, i) => <span key={i}
                                style={{ left: `${4 + i * 8}%`, animationDelay: `${i * 0.12}s` }}>
                                {SYMBOLS[SUITS[i % 4]]}</span>)}
                        </div>
                        <h3><Text id='arcade.solWon'>You won!</Text></h3>
                        <p><Text id='arcade.solFinished' fields={{ time: formatTime(elapsedMs), moves }}>
                            Finished in {formatTime(elapsedMs)} with {moves} moves.
                        </Text></p>
                        <button type='button' class='sol-control' onClick={() => this.reset()}>
                            <Text id='arcade.playAgain'>Play again</Text>
                        </button>
                    </div>}
                </div>
                <div class='sol-message' role='status'>
                    {invalid ? <Text id='arcade.solInvalid'>That move is not allowed.</Text>
                        : selected ? <Text id='arcade.solSelected'>
                            Choose a column or foundation for the selected cards.
                        </Text> : <Text id='arcade.solHint'>
                            Build down in alternating colors. Build foundations from ace to king.
                        </Text>}
                </div>
            </div>
            <p class='sol-help'><Text id='arcade.solControls'>
                Drag cards, or tap a card and then its destination. Only kings fill empty columns.
                Click the deck to draw or recycle. Tab and Enter select cards. U: undo · P: pause.
                Changing draw mode starts a new game.
            </Text></p>
            <div role='status' class='sol-score-status'>
                {submitting && <Text id='arcade.savingScore'>Saving score…</Text>}
                {saveFailed && <Text id='arcade.mineSaveFailed'>Your time could not be saved.</Text>}
                {result?.isNewBest && <Text id='arcade.mineNewBest'>New fastest time!</Text>}
            </div>
            <div class='sol-actions'>
                {this.props.onBack && <button type='button' class='btn btn-outline-light' onClick={this.back}>
                    <Text id='arcade.backToArcade'>Back to arcade</Text>
                </button>}
                <button type='button' class='btn btn-outline-light' onClick={this.close}>
                    <Text id='arcade.close'>Close</Text>
                </button>
            </div>
            <div class='sol-leaderboard'>
                {loadFailed && <div role='status'><Text id='arcade.mineLoadFailed'>
                    Fastest times could not be loaded.
                </Text></div>}
                <ArcadeLeaderboard stores={this.props.stores} game={this.game.game}
                    timed result={this.state.leaderboard} />
            </div>
        </div>;
    }
}

export default Solitaire;
